import {
  ErrorContent,
  Flex,
  HitCounter,
  LinkText,
  Loading,
  Space,
  Table,
  TablePageSizeSelector,
  TableRowMenuCell,
  Text,
} from '@/components';
import { TagTarget } from '@/features/tag/components';
import { useNavigateToWorkflowProject } from '@/features/workflow/hooks/navigate-to-workflow';
import { useWorkflowListSearchQueryStates } from '@/features/workflow/hooks/search-query-states';
import { useWorkflowStackSiderView } from '@/features/workflow/hooks/stack-sider-view';
import { useWorkflowList } from '@/features/workflow/hooks/workflow';
import { FALLBACK_TEXT, TABLE_ROW_MENU_WIDTH, dateFormat } from '@/utils';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { WorkflowActionRequiredBadge } from '../../common/WorkflowActionRequiredBadge';
import { WorkflowStatusTag } from '../../common/WorkflowStatusTag';

export const WorkflowListTable: FC = () => {
  const { t } = useTranslation();
  const stackSiderView = useWorkflowStackSiderView();
  const navigateToWorkflowProject = useNavigateToWorkflowProject();
  const [
    {
      createdAtFrom,
      createdAtTo,
      actionRequired,
      keyword,
      page,
      pageSize,
      stageLabelId,
      tagIds,
      workflowStatus,
    },
    setSearchQueryStates,
  ] = useWorkflowListSearchQueryStates();

  const workflows = useWorkflowList({
    createdAtFrom,
    createdAtTo,
    tagIds,
    stageLabelId,
    workflowStatus,
    keyword,
    actionRequired,
    page,
    pageSize,
  });

  return (
    <Flex vertical gap="middle">
      <Flex justify="space-between" align="center">
        <HitCounter count={workflows.data?.count ?? 0} label={t('relevant')} />
        <TablePageSizeSelector
          defaultPageSize={pageSize}
          onChange={(newPageSize) => {
            setSearchQueryStates((prev) => {
              return {
                ...prev,
                pageSize: newPageSize,
                page: 1,
              };
            });
          }}
        />
      </Flex>
      {workflows.isError ? (
        <ErrorContent reloadable={false} />
      ) : workflows.isPending ? (
        <Loading />
      ) : (
        <Table
          dataSource={workflows.data?.results}
          rowKey="id"
          columns={[
            {
              title: t('workflow.name'),
              key: 'title',
              dataIndex: 'title',
              width: 240,
              render: (_, record) => (
                <Space direction="vertical" size={0}>
                  {record.actionRequired && <WorkflowActionRequiredBadge />}
                  {record.title}
                </Space>
              ),
            },
            {
              title: t('tag'),
              key: 'tags',
              dataIndex: 'tags',
              width: 200,
              render: (_, record) =>
                record.tagTargets?.length
                  ? record.tagTargets?.map((target) => (
                      <TagTarget tag={target.tag} key={target.tagId} />
                    ))
                  : FALLBACK_TEXT,
            },
            {
              title: t('workflow.status-of-approval'),
              key: 'status',
              width: 160,
              render: (_, { status }) => <WorkflowStatusTag status={status} />,
            },
            {
              title: t('workflow.object-name'),
              key: 'objectName',
              width: 180,
              render: (_, record) => {
                return (
                  <Flex vertical>
                    <LinkText
                      text={record.objectName}
                      onClick={() => {
                        navigateToWorkflowProject({
                          projectId: record.objectId,
                          newTab: true,
                        });
                      }}
                      showIcon
                    />
                  </Flex>
                );
              },
            },
            {
              title: t('current-stage'),
              key: 'workflowStage',
              width: 200,
              render: (_, record) => {
                if (record.status === 'pending') {
                  return FALLBACK_TEXT;
                }
                const numOfStages = record.stages.length;
                if (record.status === 'approved') {
                  return `[${numOfStages}/${numOfStages}] ${
                    record.stages.at(-1)?.title ?? ''
                  }`;
                }
                const currentStageIndex = record.stages.findIndex(
                  (stage) => stage.status !== 'approved',
                );
                if (currentStageIndex < 0) {
                  return FALLBACK_TEXT;
                }
                const currentStage = record.stages[currentStageIndex];
                return `[${currentStageIndex + 1}/${numOfStages}] ${
                  currentStage?.title ?? FALLBACK_TEXT
                }`;
              },
            },
            {
              title: t('deadline'),
              key: 'deadline',
              dataIndex: 'deadline',
              width: 160,
              render: (value) =>
                value ? dateFormat({ datetime: value }) : t('no-deadline'),
            },
            {
              title: t('remark'),
              key: 'remark',
              dataIndex: 'remark',
              width: 200,
              render: (text) => (
                <Text text={text} weight="regular" maxWidth={400} />
              ),
            },
            {
              title: t('created-date'),
              key: 'createdAt',
              dataIndex: 'createdAt',
              width: 160,
              render: (value) => dateFormat({ datetime: value }),
            },
            {
              title: '',
              key: 'actions',
              fixed: 'right' as const,
              width: TABLE_ROW_MENU_WIDTH,
              onCell: () => ({
                style: { minWidth: TABLE_ROW_MENU_WIDTH },
              }),
              render: (_, record) => (
                <TableRowMenuCell
                  onBrowse={() => {
                    stackSiderView.set({
                      payload: [
                        {
                          config: {
                            viewType: 'workflow-details',
                            id: record.id,
                            stageViewType: 'list',
                            target: {
                              id: record.objectId,
                              key: record.key,
                            },
                          },
                          siderType: 'workflow',
                        },
                      ],
                      target: {
                        type: 'project',
                        projectId: record.objectId,
                      },
                    });
                  }}
                />
              ),
            },
          ]}
          pagination={{
            showSizeChanger: false,
            position: ['bottomRight'],
            total: workflows.data?.count,
            current: page,
            pageSize,
            onChange: (newPage, newPageSize) => {
              setSearchQueryStates((prev) => {
                return {
                  ...prev,
                  page: newPage,
                  pageSize: newPageSize,
                };
              });
            },
          }}
        />
      )}
    </Flex>
  );
};
